import React, { useState } from 'react';
import { useCourses } from '../../context/CourseContext';
interface Question {
  id: string;
  text: string;
  options: string[];
  correctAnswer: number;
}
interface AssessmentProps {
  courseId: string;
  type: 'pre' | 'post';
  onComplete: () => void;
}
// Mock questions - in a real app, these would come from an API or database
const getMockQuestions = (courseId: string, type: 'pre' | 'post'): Question[] => {
  return [{
    id: '1',
    text: 'What is the primary benefit of this course?',
    options: ['Entertainment value', 'Workplace readiness', 'Academic credits', 'Social networking'],
    correctAnswer: 1
  }, {
    id: '2',
    text: 'Which skill is most emphasized in this course?',
    options: ['Technical programming', 'Critical thinking', 'Physical fitness', 'Artistic expression'],
    correctAnswer: 1
  }, {
    id: '3',
    text: 'How can the knowledge from this course be applied?',
    options: ['Only in academic settings', 'Only in personal life', 'In both professional and personal contexts', 'Only in specific industries'],
    correctAnswer: 2
  }];
};
const Assessment: React.FC<AssessmentProps> = ({
  courseId,
  type,
  onComplete
}) => {
  const {
    completePreAssessment,
    completePostAssessment
  } = useCourses();
  const questions = getMockQuestions(courseId, type);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<number[]>(Array(questions.length).fill(-1));
  const [showResults, setShowResults] = useState(false);
  const handleAnswer = (optionIndex: number) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = optionIndex;
    setAnswers(newAnswers);
  };
  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResults(true);
    }
  };
  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };
  const handleSubmit = () => {
    // Calculate score
    const correctAnswers = questions.filter((q, index) => q.correctAnswer === answers[index]).length;
    const score = Math.round(correctAnswers / questions.length * 100);
    // Update progress
    if (type === 'pre') {
      completePreAssessment(courseId, score);
    } else {
      completePostAssessment(courseId, score);
    }
    onComplete();
  };
  if (showResults) {
    const correctAnswers = questions.filter((q, index) => q.correctAnswer === answers[index]).length;
    const score = Math.round(correctAnswers / questions.length * 100);
    return <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-56">
        <h2 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-57">
          {type === 'pre' ? 'Pre-Assessment' : 'Post-Assessment'} Results
        </h2>
        <div className="mb-6 p-4 bg-gray-50 rounded-md" data-id="element-58">
          <div className="text-center" data-id="element-59">
            <p className="text-gray-600 mb-2" data-id="element-60">Your score:</p>
            <div className="text-3xl font-bold text-blue-600 mb-2" data-id="element-61">
              {score}%
            </div>
            <p className="text-sm text-gray-500" data-id="element-62">
              {correctAnswers} out of {questions.length} questions answered
              correctly
            </p>
          </div>
        </div>
        <div className="mb-6" data-id="element-63">
          <h3 className="text-lg font-medium text-gray-700 mb-3" data-id="element-64">Summary</h3>
          {type === 'pre' ? <p className="text-gray-600" data-id="element-65">
              This assessment helps us understand your current knowledge level.
              Don't worry if you didn't score highly - that's what the course is
              for!
            </p> : <p className="text-gray-600" data-id="element-66">
              Congratulations on completing the course! This assessment helps
              measure how much you've learned during the course.
            </p>}
        </div>
        <button onClick={handleSubmit} className="w-full bg-blue-600 text-white py-2 px-4 rounded-md font-medium hover:bg-blue-700 transition-colors" data-id="element-67">
          {type === 'pre' ? 'Start Learning' : 'Get Your Certificate'}
        </button>
      </div>;
  }
  const question = questions[currentQuestion];
  return <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-68">
      <h2 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-69">
        {type === 'pre' ? 'Pre-Assessment' : 'Post-Assessment'}
      </h2>
      <div className="mb-4 flex justify-between items-center" data-id="element-70">
        <span className="text-sm font-medium text-gray-700" data-id="element-71">
          Question {currentQuestion + 1} of {questions.length}
        </span>
        <div className="flex space-x-1" data-id="element-72">
          {questions.map((_, index) => <div key={index} className={`h-2 w-6 rounded-full ${index === currentQuestion ? 'bg-blue-600' : answers[index] >= 0 ? 'bg-green-500' : 'bg-gray-300'}`} data-id="element-73"></div>)}
        </div>
      </div>
      <div className="mb-6" data-id="element-74">
        <h3 className="text-lg font-medium text-gray-700 mb-4" data-id="element-75">
          {question.text}
        </h3>
        <div className="space-y-3" data-id="element-76">
          {question.options.map((option, index) => <div key={index} onClick={() => handleAnswer(index)} className={`p-3 rounded-md border cursor-pointer transition-colors ${answers[currentQuestion] === index ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-300'}`} data-id="element-77">
              <label className="flex items-start cursor-pointer" data-id="element-78">
                <input type="radio" className="mt-1" checked={answers[currentQuestion] === index} onChange={() => handleAnswer(index)} data-id="element-79" />
                <span className="ml-2 text-gray-700" data-id="element-80">{option}</span>
              </label>
            </div>)}
        </div>
      </div>
      <div className="flex justify-between" data-id="element-81">
        <button onClick={handlePrevious} disabled={currentQuestion === 0} className={`px-4 py-2 rounded-md font-medium ${currentQuestion === 0 ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`} data-id="element-82">
          Previous
        </button>
        <button onClick={handleNext} disabled={answers[currentQuestion] === -1} className={`px-4 py-2 rounded-md font-medium ${answers[currentQuestion] === -1 ? 'bg-blue-300 text-white cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`} data-id="element-83">
          {currentQuestion < questions.length - 1 ? 'Next' : 'See Results'}
        </button>
      </div>
    </div>;
};
export default Assessment;